import { createFileRoute, notFound } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { getPage } from "@/content/pages";
import { PageHero } from "@/components/PageHero";
import { ContactForm } from "@/components/ContactForm";
import { CtaBand } from "@/components/sections/CtaBand";
import { RelatedNews } from "@/components/sections/RelatedNews";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import ServiceFaqsImage from "@/assets/service-faqs.jpg";

const PAGE_KEY = "/faqs";

function extractFaqs(body: string): { q: string; a: string }[] {
  const lines = body.split("\n");
  const items: { q: string; a: string }[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const m = /^##\s+(.+\?)\s*$/.exec(line);
    if (!m) continue;
    const q = m[1].replace(/\s+/g, " ").trim();
    const answerLines: string[] = [];
    for (let j = i + 1; j < lines.length; j++) {
      const next = lines[j];
      if (/^#{1,6}\s/.test(next)) break;
      answerLines.push(next);
    }
    const a = answerLines
      .join(" ")
      .replace(/We use cookies[\s\S]*$/i, "")
      .replace(/\s+/g, " ")
      .trim();
    if (a) items.push({ q, a });
  }
  // de-duplicate by question, keep first occurrence
  const seen = new Set<string>();
  return items.filter((it) => {
    const key = it.q.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export const Route = createFileRoute("/faqs")({
  loader: () => {
    const page = getPage(PAGE_KEY);
    if (!page) throw notFound();
    return page;
  },
  head: ({ loaderData }) => ({
    meta: loaderData
      ? [
          { title: loaderData.title },
          { name: "description", content: loaderData.description },
          { property: "og:title", content: loaderData.title },
          { property: "og:description", content: loaderData.description },
      { property: "og:url", content: "https://www.phil-carr.co.uk/faqs" },
        ]
      : [],
    links: [{ rel: "canonical", href: "https://www.phil-carr.co.uk/faqs" }],
    scripts: loaderData
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: extractFaqs(loaderData.body).map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }),
          },
        ]
      : [],
  }),
  notFoundComponent: () => (
    <div className="container mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-display text-4xl">Page not found</h1>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="container mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-display text-3xl">Something went wrong</h1>
      <p className="mt-3 text-muted-foreground">{error.message}</p>
    </div>
  ),
  component: Page,
});

function Page() {
  const page = Route.useLoaderData();
  const faqs = extractFaqs(page.body);
  const pageTitle = page.body.match(/^#\s+(.+)$/m)?.[1]?.trim() ?? page.title;
  return (
    <>
      <PageHero title={pageTitle} />

      {/* Intro row with supporting image */}
      <section className="container mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
        <div className="row g-5 align-items-center">
          <div className="col-lg-6">
            <p className="demo-section-label">SEO answers, no jargon</p>
            <h2 className="site-heading-md mt-4 font-display font-light">
              Straightforward answers about <em>pricing</em>, <em>timelines</em> and how SEO
              support actually works
            </h2>
            <p className="site-copy mt-6 text-muted-foreground">
              Everything you need to know about pricing, timelines, and how SEO support works for
              Barnsley and UK businesses. Compare options on the{" "}
              <Link to="/services" className="text-primary underline-offset-4 hover:underline">
                services page
              </Link>{" "}
              or{" "}
              <Link to="/contact" className="text-primary underline-offset-4 hover:underline">
                contact me
              </Link>{" "}
              for practical guidance on what to do first.
            </p>
          </div>
          <div className="col-lg-6 mt-10 lg:mt-0">
            <div className="relative aspect-[16/10] overflow-hidden rounded-[10px] border border-primary/30 shadow-[0_30px_80px_-40px_rgba(179,255,10,0.35)]">
              <img
                src={ServiceFaqsImage}
                alt="Open notebook, pen, coffee and reading glasses on a wooden desk"
                loading="eager"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Accordion FAQs */}
      <section className="demo-faq-section border-y border-border">
        <div className="container mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <div className="mx-auto max-w-5xl text-center">
              <p className="demo-section-label">FAQs</p>
              <h2 className="site-heading-md mt-4 font-display font-light">
                {faqs.length} answers to the questions I hear the <em>most</em>
              </h2>
              <p className="site-copy mx-auto mt-6 max-w-3xl text-muted-foreground">
                Tap any question to expand. Still need clarity? Get in touch and I'll walk you
                through it directly.
              </p>
              <Link
                to="/contact"
                className="mt-8 inline-flex demo-button-primary items-center gap-3 px-7 py-4 text-sm font-semibold transition-transform hover:-translate-y-0.5"
              >
                Ask a question
              </Link>
          </div>
          <Accordion type="single" collapsible className="demo-accordion mt-12 w-full">
            {faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`} className="demo-accordion-item">
                <AccordionTrigger className="demo-accordion-trigger group relative flex w-full text-left font-display hover:no-underline [&>svg.lucide-chevron-down]:hidden">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="demo-accordion-content text-base leading-relaxed">
                  <p>{f.a}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <RelatedNews
        count={3}
        slugs={[
          "how-to-hire-an-seo-consultant",
          "common-seo-mistakes-that-hold-small-businesses-back",
          "how-seo-builds-momentum-over-time-for-small-businesses",
        ]}
      />

      <section className="inner-contact-section bg-background/95 no-bg-shape">
        <div className="container mx-auto max-w-6xl px-6 py-16 sm:px-8 lg:px-10 lg:py-24">
          <div className="row g-5 align-items-start">
            <div className="col-lg-5">
              <p className="demo-section-label">Get in touch</p>
              <h2 className="site-heading-lg mt-4 font-display font-light">
                Ask your own <em>question</em>
              </h2>
              <p className="site-copy mt-6 text-muted-foreground">
                Prefer a direct answer? Send me your question and I'll reply with practical
                guidance — no obligation.
              </p>
            </div>
            <div className="col-lg-7">
              <ContactForm pageContext={page.title} />
            </div>
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
